/* eslint-disable react-refresh/only-export-components --
 * Provider and consumer hook are intrinsically paired, same shape as
 * @/lib/pending-edits.
 */
import { createContext, useContext } from "react";
import type { ReactNode } from "react";
import { useQuery } from "@apollo/client/react";
import { ReachableFeaturesDocument } from "@/graphql/generated/operations";

// Effective feature keys come from Genesis Role Permissions. Registration receives
// the configured Group workspace immediately; no identity or content-sharing policy
// is reconstructed here. Role edits refetch active queries, including this one.
//
// Fail-closed by construction: while loading or on any error, `data` is
// undefined, so `featureKeys` resolves to an empty array and `hasFeature`
// answers false for everything. There is no separate loading/error branch to
// get wrong for hiding a nav item or skipping a query.
//
// `loading` is exposed separately because a REDIRECT is not safe to fire on
// the same "false while loading" rule: RequireFeature renders on the very
// first paint, before the network round trip resolves, and firing
// Navigate() then would bounce every visitor away from a page they're
// allowed to see, on every reload, before the answer ever arrives. Only
// hasFeature (used for hiding/skipping, never for navigating away) may treat
// loading as "false" - a redirect decision has to wait for `loading` to
// clear first.
interface ContextValue {
  hasFeature: (key: string) => boolean;
  // A request for the whitelist is in flight, the first one or a refetch. What a one-off landing
  // decision (GrowOverviewRedirect) waits for: it should land on the answer that is coming.
  loading: boolean;
  // No whitelist yet: the first answer has not arrived. What a guard that keeps a page mounted
  // (RequireFeature) waits for. A refetch of all active queries (after an import save, a role edit, a
  // locale flip) keeps the previous whitelist; had the guard waited on `loading` instead, it rendered
  // nothing for that moment and remounted the whole page, dropping all local state on it (spark#312).
  pending: boolean;
}

const AccessFeaturesContext = createContext<ContextValue>({
  hasFeature: () => false,
  loading: false,
  pending: false,
});

export function AccessFeaturesProvider({ children }: { children: ReactNode }) {
  const { data, loading } = useQuery(ReachableFeaturesDocument, {
    fetchPolicy: "network-only",
  });
  const featureKeys = data?.reachableFeatures.featureKeys ?? [];
  // No useMemo: this provider re-renders only when the query result changes
  // (once per session in practice), never in a hot render loop, so a fresh
  // closure per render costs nothing worth guarding against.
  const value: ContextValue = {
    hasFeature: (key) => featureKeys.includes(key),
    loading,
    pending: loading && !data,
  };

  return (
    <AccessFeaturesContext.Provider value={value}>
      {children}
    </AccessFeaturesContext.Provider>
  );
}

export function useHasFeature(key: string): boolean {
  return useContext(AccessFeaturesContext).hasFeature(key);
}

// For call sites that need to check more than one key without calling a hook
// per key inside a loop (React's rules of hooks forbid that) - e.g. filtering
// a list of nav items, each carrying its own feature key.
export function useAccessFeatures(): ContextValue {
  return useContext(AccessFeaturesContext);
}
